import * as React from 'react'

import type { OpeningTemplateEffectiveness as OpeningTemplateEffectivenessData } from '@sdr-agend/admin-schemas'

import { formatPercentOrDash } from '@/lib/format'

/**
 * "Efetividade do template de abertura" (2026-07-21, user request) — tabela
 * por campanha/variante do funil global mostrado em `Dashboard.tsx` (Mensagens
 * Enviadas/Respondidas/Taxa de Resposta/Reuniões Agendadas/Taxa de
 * Agendamento). Só a parte "por template" mora aqui — os 5 cards globais são
 * montados em `Dashboard.tsx` com `KpiCard`, mesmo padrão das outras seções.
 */
export interface OpeningTemplateEffectivenessProps {
  data: OpeningTemplateEffectivenessData
}

export function OpeningTemplateEffectiveness({
  data,
}: OpeningTemplateEffectivenessProps): React.ReactElement {
  if (data.byTemplate.length === 0) {
    return (
      <div
        className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="opening-template-effectiveness-empty"
      >
        Nenhuma mensagem de abertura enviada por campanhas de Lead Novo no período selecionado.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto" data-testid="opening-template-effectiveness">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="pb-1.5 pr-3 font-medium">Campanha</th>
            <th className="pb-1.5 pr-3 font-medium">Variante</th>
            <th className="pb-1.5 pr-3 font-medium">Template</th>
            <th className="pb-1.5 pr-3 text-right font-medium">Enviadas</th>
            <th className="pb-1.5 pr-3 text-right font-medium">Respondidas</th>
            <th className="pb-1.5 pr-3 text-right font-medium">Taxa Resp.</th>
            <th className="pb-1.5 pr-3 text-right font-medium">Agendadas</th>
            <th className="pb-1.5 text-right font-medium">Taxa Agend.</th>
          </tr>
        </thead>
        <tbody>
          {data.byTemplate.map((row, i) => (
            <tr
              key={`${row.campaignId}:${row.variantKey ?? 'default'}`}
              className="border-b last:border-0"
              data-testid={`opening-template-row-${i}`}
            >
              <td className="py-1.5 pr-3 font-medium">{row.campaignName}</td>
              <td className="py-1.5 pr-3 text-muted-foreground">{row.variantKey ?? '—'}</td>
              <td className="py-1.5 pr-3 text-muted-foreground">{row.templateName ?? '—'}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums">
                {row.messagesSent.toLocaleString('pt-BR')}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums">
                {row.messagesAnswered.toLocaleString('pt-BR')}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums">
                {formatPercentOrDash(row.responseRate)}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums">
                {row.meetingsScheduled.toLocaleString('pt-BR')}
              </td>
              <td className="py-1.5 text-right tabular-nums">
                {formatPercentOrDash(row.schedulingRate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default OpeningTemplateEffectiveness
